import { useEffect, useState } from "react";

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { HACK_columnMatching } from "./use-column-ordering";

interface ColumnOrderModalProps {
  setColumnModelOpen: (isOpen: boolean) => void;
  columnOrderModalOpen: boolean;
  columnOrdering: string[];
  columns: any;
  setColumnOrdering: (arg0: string[]) => void;
}

export const ColumnOrderModal = ({
  columnOrderModalOpen,
  setColumnModelOpen,
  columnOrdering,
  columns,
  setColumnOrdering,
}: ColumnOrderModalProps) => {
  const [tempOrdering, setTempOrdering] = useState<string[]>(columnOrdering);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const indices = columnOrdering.map((columnKey) =>
      columns.findIndex((column: any) =>
        HACK_columnMatching(column.key, columnKey),
      ),
    );

    if (
      indices.find((el) => el === -1) ||
      columnOrdering.length !== columns.length
    ) {
      setTempOrdering(columns.map((column: any) => column.key));
    } else {
      setTempOrdering(columnOrdering);
    }
  }, [columnOrdering, setTempOrdering, columns]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = tempOrdering.findIndex((columnKey) =>
        HACK_columnMatching(columnKey, active.id as string),
      );
      const newIndex = tempOrdering.findIndex((columnKey) =>
        HACK_columnMatching(columnKey, over?.id as string),
      );

      const newOrdering = arrayMove(tempOrdering, oldIndex, newIndex);
      setTempOrdering(newOrdering);
    }
  };

  const onSave = () => {
    setColumnModelOpen(false);
    setColumnOrdering(tempOrdering);
  };

  return (
    <>
      <PrimaryButton className="mr-3" onClick={() => setColumnModelOpen(true)}>
        CHANGE COLUMN ORDER
      </PrimaryButton>
      <Modal
        isOpen={columnOrderModalOpen}
        onClose={() => setColumnModelOpen(false)}
        titleContent={<div className="font-bold">Change column order</div>}
        hideCloseButton
        size="sm"
        contentClassName="overflow-hidden"
      >
        <div className="mb-4 h-[35vh] overflow-auto">
          <table className="table table-bordered bg-white">
            <tbody>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={tempOrdering}>
                  {tempOrdering.map((columnKey) => {
                    return (
                      <ColumnOrderRow
                        columnKey={columnKey}
                        columns={columns}
                        key={columnKey}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </tbody>
          </table>
        </div>
        <PrimaryButton onClick={onSave}>SAVE</PrimaryButton>
        <TertiaryButton onClick={() => setColumnModelOpen(false)}>
          CLOSE
        </TertiaryButton>
      </Modal>
    </>
  );
};

const ColumnOrderRow = ({
  columnKey,
  columns,
}: {
  columnKey: string;
  columns: any[];
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: columnKey,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr id={columnKey} ref={setNodeRef} style={style}>
      <td {...attributes} {...listeners} className="text-center align-middle">
        <i className="fa-regular fa-sharp fa-grip-vertical" />
      </td>
      <td>
        {
          columns.find((column) => HACK_columnMatching(column.key, columnKey))
            .name
        }
      </td>
    </tr>
  );
};
